import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import api from "../utils/api";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// Helper functions to handle token storage
const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem("authToken", token);
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    localStorage.removeItem("authToken");
    delete api.defaults.headers.common["Authorization"];
  }
};

const getStoredToken = () => {
  return localStorage.getItem("authToken");
};

const setStoredUser = (user) => {
  if (user) {
    localStorage.setItem("user", JSON.stringify(user));
  } else {
    localStorage.removeItem("user");
  }
};

const getStoredUser = () => {
  const storedUser = localStorage.getItem("user");
  return storedUser ? JSON.parse(storedUser) : null;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getStoredUser());
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !!getStoredToken()
  );
  const [loading, setLoading] = useState(true);
  const isInitialCheckDone = useRef(false);
  const maxRetries = 3;
  const retryDelay = 1000; // 1 second

  // Set token in axios headers when component mounts
  useEffect(() => {
    const token = getStoredToken();
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }, []);

  const checkAuth = async (retries = 0) => {
    try {
      // console.log("Performing auth check with /auth/me, attempt:", retries + 1);
      const res = await api.get("/auth/me", { skipAuthRedirect: true });
      // console.log("Auth check success:", res.data.user);
      setUser(res.data.user);
      setStoredUser(res.data.user);
      setIsAuthenticated(true);
    } catch (err) {
      // console.log("Auth check failed:", err.response?.data || err.message);
      if (retries < maxRetries && err.response?.status === 401) {
        // console.log(`Retrying auth check in ${retryDelay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        return checkAuth(retries + 1);
      }
      setUser(null);
      setStoredUser(null);
      setAuthToken(null);
      setIsAuthenticated(false);
    } finally {
      // console.log("Auth check complete, setting loading to false");
      setLoading(false);
      isInitialCheckDone.current = true;
    }
  };

  useEffect(() => {
    if (isInitialCheckDone.current) {
      // console.log("Initial auth check already performed, skipping");
      return;
    }
    checkAuth();
  }, []);

  const adminLogin = async (email, password) => {
    try {
      // console.log("Attempting admin login for:", email);
      const res = await api.post("/auth/admin/login", { email, password });
      if (res.data.success) {
        // console.log("Admin login successful:", res.data.user);
        setUser(res.data.user);
        setStoredUser(res.data.user);
        setAuthToken(res.data.token);
        setIsAuthenticated(true);
        return true;
      }
      // console.log("Admin login failed");
      return false;
    } catch (err) {
      console.error("Admin login error:", err.response?.data || err);
      return false;
    }
  };

  const userLogin = async (userId, password) => {
    try {
      // console.log("Attempting user login for userId:", userId);
      const res = await api.post("/auth/user/login", { userId, password });
      if (res.data.success) {
        setUser(res.data.user);
        setStoredUser(res.data.user);
        setAuthToken(res.data.token);
        setIsAuthenticated(true);
        return res.data.user;
      }
      // console.log("User login failed");
      return false;
    } catch (err) {
      console.error("User login error:", err.response?.data || err);
      return false;
    }
  };

  const logout = async () => {
    try {
      //   // console.log("Attempting logout");
      const res = await api.post("/auth/logout");
      return res.data.success;
    } catch (err) {
      console.error("Logout error:", err.response?.data || err);
    } finally {
      //   // console.log("Resetting auth state");
      setUser(null);
      setStoredUser(null);
      setAuthToken(null);
      setIsAuthenticated(false);
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  // Format currency helper
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,
        adminLogin,
        userLogin,
        logout,
        formatDate,
        formatCurrency,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
